import Webcam from 'react-webcam';
import { Flex } from '..';
import { useCallback, useRef, useState } from 'react';
import { CaptureButton } from './CaptureButton';
import { convertBase64ToFile } from '@/utils/func';
import { useTranslation } from 'react-i18next';

export interface ImageCaptureProps {
  quality?: number;
  loading?: boolean;
  format?: 'image/webp' | 'image/png' | 'image/jpeg';
  onConfirm?: (file: File) => void;
  onError?: (error: string | DOMException) => void;
}

export function ImageCapture({
  quality = 1,
  format = 'image/png',
  loading = undefined,
  onConfirm = undefined,
  onError
}: ImageCaptureProps) {

  const webcamRef = useRef<Webcam>(null);
  const [src, setSrc] = useState(null);
  const [file, setFile] = useState(null);
  const [t] = useTranslation(['face']);

  const onCaptureClick = useCallback(async () => {
    if (!webcamRef.current) return;
    setSrc(null);
    const imageSrc = webcamRef.current.getScreenshot();
    setSrc(imageSrc);
    const file = await convertBase64ToFile(imageSrc, 'image.png', 'image/png');
    setFile(file);
  }, [webcamRef]);

  return (
    <Flex direction="column" className="w-full h-full relative">
      {!src ?
        <Webcam
          ref={webcamRef}
          audio={false}
          playsInline
          screenshotFormat={format}
          screenshotQuality={quality}
          forceScreenshotSourceSize={true}
          onUserMediaError={onError}
          className="h-full object-cover max-w-full max-h-full rounded-lg"
        /> :
        <img
          src={src}
          className="max-w-[100%] max-h-[100%] h-full object-cover" />
      }
      <CaptureButton
        src={src}
        loading={loading}
        onRemove={() => setSrc(null)}
        onClick={onCaptureClick}
        captureText={t('face:labels.take-photo')}
        removeText={t('face:labels.remove-photo')}
        onConfirm={() => {
          if (onConfirm) {
            onConfirm(file);
          }
        }}
      />
    </Flex>
  );
}

export default ImageCapture;
